﻿import type { GameConfig } from '../types';

export type CombatState = {
  enemyMaxHp: number;
  enemyHp: number;
  jumpCount: number;
  targetJumps: number;
  defeated: boolean;
};

export function createCombatState(config: Pick<GameConfig, 'enemyMaxHp' | 'targetJumps'>): CombatState {
  assertPositive(config.enemyMaxHp, 'enemyMaxHp');
  assertPositive(config.targetJumps, 'targetJumps');
  return {
    enemyMaxHp: config.enemyMaxHp,
    enemyHp: config.enemyMaxHp,
    jumpCount: 0,
    targetJumps: config.targetJumps,
    defeated: false
  };
}

export function damagePerJump(state: Pick<CombatState, 'enemyMaxHp' | 'targetJumps'>): number {
  assertPositive(state.enemyMaxHp, 'enemyMaxHp');
  assertPositive(state.targetJumps, 'targetJumps');
  return state.enemyMaxHp / state.targetJumps;
}

export function applyJumpDamage(state: CombatState, jumps = 1): CombatState {
  if (state.defeated) return { ...state };
  const safeJumps = Math.max(0, Math.floor(jumps));
  if (safeJumps === 0) return { ...state };

  const nextJumpCount = state.jumpCount + safeJumps;
  const nextHp = Math.max(0, state.enemyHp - damagePerJump(state) * safeJumps);
  const defeated = nextHp <= defeatEpsilon(state.enemyMaxHp);

  return {
    ...state,
    jumpCount: nextJumpCount,
    enemyHp: defeated ? 0 : nextHp,
    defeated
  };
}

export function healEnemy(state: CombatState, amount: number): CombatState {
  if (state.defeated) return { ...state };
  const safeAmount = Number.isFinite(amount) ? Math.max(0, amount) : 0;
  if (safeAmount === 0) return { ...state };
  return {
    ...state,
    enemyHp: Math.min(state.enemyMaxHp, state.enemyHp + safeAmount)
  };
}

export function hpPercent(state: Pick<CombatState, 'enemyHp' | 'enemyMaxHp'>): number {
  assertPositive(state.enemyMaxHp, 'enemyMaxHp');
  return Math.max(0, Math.min(100, (state.enemyHp / state.enemyMaxHp) * 100));
}

function assertPositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a positive finite number`);
  }
}

function defeatEpsilon(enemyMaxHp: number): number {
  return Math.max(Number.EPSILON, enemyMaxHp * 1e-10);
}
